/**
 * The lesson's record card: three numbers and a three-star rating, read straight from the lesson's
 * stored blob. See specs/scoreboard.md.
 *
 * Pure derivations, like lessonStats.ts and for the same reason: the panel is opened from the menu,
 * which already holds a `ReadingProgression` for the active lesson, but the rating math must also
 * be testable — and readable for a lesson nobody is playing — without constructing an engine.
 * `ReadingProgression` owns the WRITES (recordBoardCleared / recordGameEnd) and reads its own
 * profile through `scoreboardOf` here, so the two can never keep different rules.
 */
import type { Pack } from '@/data/packs/types';
import { packKey } from '@/data/packs';
import { GALAXY_STARS } from '@/game/milestones';
import { loadMastery, type ProfileStat } from './store';
import { getScoreboardSeen, setScoreboardSeen } from '@/game/storage';

/**
 * The bar for the speed star: a board cleared in under five seconds, strictly.
 *
 * Five seconds is about where a fluent adult reads (3–9 s a board, measured in
 * specs/adaptive-difficulty.md §7) and far out of a beginner's reach (40–55 s) — deliberately, since
 * this is the one star that means "reading got fast". It is measured on RoundScene's pause-aware
 * board clock, so a board left open behind the help overlay cannot earn it.
 */
export const FAST_ROUND_MS = 5000;

/** Stars in the rating. Three, one per KIND of achievement — see specs/scoreboard.md §3. */
export const RATING_STARS = 3;

/** One lesson's records. All four marks are per lesson; see store.ts's ProfileStat. */
export interface Scoreboard {
  /** Fastest board cleared in this lesson, ms. Null until the first clear. */
  bestRoundMs: number | null;
  /** Highest round number reached in one game (the round a game ended on). */
  maxRound: number;
  /** Boards cleared in this lesson, lifetime. */
  totalRounds: number;
  /** Most planet-stack stars in one game. GALAXY_STARS is the top of the ladder. */
  bestStackStars: number;
  /** The journey was completed at some point. Persisted, so it survives star regression. */
  journeyDone: boolean;
  /**
   * Minigame unlocks EARNED over this lesson's lifetime — not the banks' current balance. The panel
   * shows how many times the child has opened each treat, so spending one must not take it back.
   */
  ufos: number;
  diamonds: number;
  cubes: number;
  galaxies: number;
  satellites: number;
  plates: number;
}

/** Which of the three rating stars are earned, and how many. */
export interface Rating {
  /** A round under FAST_ROUND_MS. */
  fast: boolean;
  /** The galaxy reached in a single game. */
  galaxy: boolean;
  /** Every word of the lesson mastered. */
  complete: boolean;
  /** 0..RATING_STARS. */
  stars: number;
}

export function emptyScoreboard(): Scoreboard {
  return {
    bestRoundMs: null, maxRound: 0, totalRounds: 0, bestStackStars: 0, journeyDone: false,
    ufos: 0, diamonds: 0, cubes: 0, galaxies: 0, satellites: 0, plates: 0,
  };
}

/** A lesson's records, straight off its stored profile. */
export function scoreboardOf(profile: ProfileStat): Scoreboard {
  return {
    // `?? ` throughout: every one of these fields is absent on a save written before the feature,
    // and a record card showing NaN is worse than one showing nothing.
    bestRoundMs: profile.bestRoundMs ?? null,
    maxRound: profile.maxRound ?? 0,
    totalRounds: profile.totalRounds ?? 0,
    bestStackStars: profile.bestStackStars ?? 0,
    journeyDone: profile.abcPlanetReachedAt !== null,
    // The UFO has two independent sources and therefore two counters — the star-driven high-water
    // mark and the once-per-run Mars bonus (see store.ts). "How many did I unlock" is their sum;
    // `ufosClaimed` is deliberately NOT subtracted, here or for the other two: this is a tally of
    // what was earned, not what is left to spend.
    ufos: (profile.ufosUnlocked ?? 0) + (profile.ufosBonus ?? 0),
    diamonds: profile.diamondsEarned ?? 0,
    cubes: profile.cubesEarned ?? 0,
    galaxies: profile.galaxiesEarned ?? 0,
    satellites: profile.satellitesEarned ?? 0,
    plates: profile.platesEarned ?? 0,
  };
}

/** One lesson's records, read from storage without touching it (the menu path). */
export function lessonScoreboard(pack: Pack): Scoreboard {
  return scoreboardOf(loadMastery(packKey(pack)).profile);
}

/**
 * The three-star rating. Speed (one board), endurance (one game), persistence (the whole journey) —
 * three different kinds of achievement, earnable in any order, none a prerequisite of another.
 *
 * Every input is a high-water mark, so a star once earned can never be lost by playing badly.
 */
export function ratingFor(sb: Scoreboard): Rating {
  const fast = sb.bestRoundMs !== null && sb.bestRoundMs < FAST_ROUND_MS;
  const galaxy = sb.bestStackStars >= GALAXY_STARS;
  const complete = sb.journeyDone;
  return { fast, galaxy, complete, stars: Number(fast) + Number(galaxy) + Number(complete) };
}

/**
 * Everything the card DISPLAYS, in one short string. Two signatures differ exactly when the card
 * would be a different card — which is the question the menu's pulsing target asks, and the reason
 * this is derived from the numbers rather than being a "records changed" flag somebody has to
 * remember to set at every write site.
 */
export function scoreboardSignature(sb: Scoreboard): string {
  return [
    sb.bestRoundMs ?? '-', sb.maxRound, sb.totalRounds, sb.bestStackStars,
    sb.journeyDone ? 1 : 0, sb.ufos, sb.diamonds, sb.cubes, sb.galaxies, sb.satellites, sb.plates,
  ].join('|');
}

/**
 * The lesson's records have moved since the card was last opened — the menu's cue to pulse the
 * reward target (specs/scoreboard.md §5.5).
 *
 * False for a lesson with no records at all: an empty card is not something to be invited to read,
 * and this is also what keeps a progress reset from leaving the target pulsing at nothing.
 */
export function hasUnseenRecords(pack: Pack): boolean {
  const sb = lessonScoreboard(pack);
  if (!hasRecords(sb)) return false;
  return getScoreboardSeen(packKey(pack)) !== scoreboardSignature(sb);
}

/** Records read: the card was opened showing exactly `sb`. Called by the panel itself, so no
 * entry point can forget it. */
export function markScoreboardSeen(pack: Pack, sb: Scoreboard): void {
  setScoreboardSeen(packKey(pack), scoreboardSignature(sb));
}

/**
 * Whether a lesson has anything to show yet — used to decide between the panel's numbers and its
 * "no round played yet" line. A lesson that was played but never had a board CLEARED (every game
 * abandoned on the first board) still counts as played: `maxRound` is what those games moved.
 */
export function hasRecords(sb: Scoreboard): boolean {
  return sb.totalRounds > 0 || sb.maxRound > 0 || sb.bestRoundMs !== null;
}
